import { Link } from 'react-router-dom';
import { MetaSEO } from '@/components/common/MetaSEO';
import { Card } from '@/components/ui/card';
import { Shield, Mail, Database, Lock, Eye, UserX, Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const LEGAL_LAST_UPDATED = '2026-03-31T00:00:00Z';

const INTL_LOCALE_MAP: Record<string, string> = {
  pt: 'pt-PT',
  en: 'en-US',
  fr: 'fr-FR',
  es: 'es-ES',
};

interface PrivacySectionContent {
  title: string;
  personal_title?: string;
  personal_items?: string[];
  technical_title?: string;
  technical_items?: string[];
  cookies_title?: string;
  cookies_items?: string[];
  items?: string[];
  cta_prefix?: string;
  cta_label?: string;
  cta_suffix?: string;
  body?: string;
  intro?: string;
  outro?: string;
}

interface PrivacyTranslationShape {
  seo_title: string;
  seo_description: string;
  page_title: string;
  last_updated_label: string;
  intro: string;
  sections: {
    information_we_collect: PrivacySectionContent;
    how_we_use_information: PrivacySectionContent;
    data_security: PrivacySectionContent;
    data_retention: PrivacySectionContent;
    your_rights: PrivacySectionContent;
    third_party_services: PrivacySectionContent;
    international_transfers: PrivacySectionContent;
    contact_us: PrivacySectionContent;
    policy_changes: PrivacySectionContent;
  };
}

const renderHtmlList = (items: string[]) => (
  <ul className="list-disc list-inside space-y-2 text-muted-foreground">
    {items.map((item) => (
      <li key={item} dangerouslySetInnerHTML={{ __html: item }} />
    ))}
  </ul>
);

export default function Privacy() {
  const { t, i18n } = useTranslation();
  const content = t('privacy', { returnObjects: true }) as PrivacyTranslationShape;
  const locale = INTL_LOCALE_MAP[i18n.resolvedLanguage || 'pt'] || INTL_LOCALE_MAP.pt;
  const formattedDate = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(LEGAL_LAST_UPDATED));

  const sections = [
    { id: 'information-we-collect', icon: Database, content: content.sections.information_we_collect },
    { id: 'how-we-use-information', icon: Eye, content: content.sections.how_we_use_information },
    { id: 'data-security', icon: Lock, content: content.sections.data_security },
    { id: 'data-retention', icon: Database, content: content.sections.data_retention },
    { id: 'your-rights', icon: Shield, content: content.sections.your_rights },
    { id: 'third-party-services', icon: Mail, content: content.sections.third_party_services },
    { id: 'international-transfers', icon: Download, content: content.sections.international_transfers },
    { id: 'contact-us', icon: UserX, content: content.sections.contact_us },
  ];

  return (
    <>
      <MetaSEO
        title={content.seo_title}
        description={content.seo_description}
        canonical="https://www.babete.pt/privacy"
        schema={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: content.page_title,
          description: content.seo_description,
          image: "https://www.babete.pt/og-image.png",
          url: "https://www.babete.pt/privacy",
        }}
      />

      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="h-8 w-8 text-primary" />
            <h1 className="text-4xl font-bold">{content.page_title}</h1>
          </div>
          <p className="text-muted-foreground">
            {content.last_updated_label}: {formattedDate}
          </p>
        </div>

        <Card className="p-6 mb-6">
          <p className="text-lg mb-4">{content.intro}</p>
        </Card>

        <article className="space-y-8">
          {sections.map(({ id, icon: Icon, content: sectionContent }) => (
            <section key={id} id={id}>
              <div className="flex items-center gap-2 mb-4">
                <Icon className="h-6 w-6 text-primary" />
                <h2 className="text-2xl font-semibold">{sectionContent.title}</h2>
              </div>
              <Card className="p-6 space-y-4">
                {sectionContent.intro && <p className="text-muted-foreground">{sectionContent.intro}</p>}

                {sectionContent.personal_title && (
                  <div>
                    <h3 className="font-semibold mb-3">{sectionContent.personal_title}</h3>
                    {renderHtmlList(sectionContent.personal_items || [])}
                  </div>
                )}

                {sectionContent.technical_title && (
                  <div>
                    <h3 className="font-semibold mb-3">{sectionContent.technical_title}</h3>
                    {renderHtmlList(sectionContent.technical_items || [])}
                  </div>
                )}

                {sectionContent.cookies_title && (
                  <div>
                    <h3 className="font-semibold mb-3">{sectionContent.cookies_title}</h3>
                    {renderHtmlList(sectionContent.cookies_items || [])}
                  </div>
                )}

                {sectionContent.items && renderHtmlList(sectionContent.items)}

                {sectionContent.body && <p className="text-muted-foreground">{sectionContent.body}</p>}

                {sectionContent.cta_prefix && sectionContent.cta_label && sectionContent.cta_suffix && (
                  <p className="text-sm">
                    {sectionContent.cta_prefix}{' '}
                    <Link to="/settings" className="underline text-primary">
                      {sectionContent.cta_label}
                    </Link>{' '}
                    {sectionContent.cta_suffix}
                  </p>
                )}

                {sectionContent.outro && <p className="text-sm text-muted-foreground">{sectionContent.outro}</p>}

                {id === 'contact-us' && (
                  <ul className="list-none space-y-2 text-muted-foreground">
                    <li><strong>Email:</strong> privacy@babete.pt</li>
                    <li><strong>Support:</strong> support@babete.pt</li>
                  </ul>
                )}
              </Card>
            </section>
          ))}

          <section id="policy-changes">
            <Card className="p-6 bg-muted">
              <h2 className="text-xl font-semibold mb-3">{content.sections.policy_changes.title}</h2>
              <p className="text-muted-foreground">{content.sections.policy_changes.body}</p>
            </Card>
          </section>
        </article>
      </main>
    </>
  );
}
